import { useEffect, useRef, useState } from 'react'
import {
  ActivityIndicator,
  Image,
  Modal,
  Platform,
  ScrollView,
} from 'react-native'

import { useRoute } from '@react-navigation/native'
import { useQueryClient } from '@tanstack/react-query'
import { publishEvent } from '@zet/analytics'
import { useToast } from '@zet/common/components/toast'
import { useIsSsoLoggedInUser } from '@zet/common/hooks/useIsSsoLoggedInUser'
import { useNavigation } from '@zet/common/hooks/useNavigation'
import { MY_TEAMS_ADVISOR } from '@zet/constants'
import { useFetchCustomerDetails } from '@zet/customer/src/api/hooks'
import useMyCustomersStore from '@zet/customer/src/store/myCustomers'
import { BottomSheet, Icon, colors as zetColors } from '@zet/design-system'
import { Button } from '@zet/design-system/button'
import { Stack, XStack, YStack } from '@zet/design-system/layout'
import Pressable from '@zet/design-system/pressable'
import { B2, B5, SH1, SH2, SH3, SH4 } from '@zet/design-system/typography'
import { APP_NAME } from '@zet/end-points'
import {
  useBrandDetailsById,
  useGenerateGlobalSharableLink,
  useGenerateSharableLink,
  useShareCreative,
} from '@zet/feature-brands/api/brands/hooks'
import useBrandStore from '@zet/feature-brands/store/brands'
import { useUserContext } from '@zet/feature-user'
import { i18n } from '@zet/localization'
import EnglishLanguage from '@zet/svg/englishLanguage'
import HindiLanguage from '@zet/svg/hindiLanguage'
import RegistrationForm from '@zet/svg/registrationForm'
import RegistrationFormCheck from '@zet/svg/registrationFormCheck'
import { LoggedInNavigation } from '@zet/utils/interface'
import { navigationRef } from '@zet/utils/navigation'
import { shareToApp } from '@zet/utils/shareLink'
import storageUtil from '@zet/utils/storage'
import BlobUtil from 'react-native-blob-util'

import { ShareButtons } from '../../components/shareButtons'
import UserConsent from '../../components/userConsent'
import { normalizeMultilineText } from '../../utils'
import ArnBottomSheet from './arnBottomSheet'

const ShareCardLink = () => {
  const { params } = useRoute()
  const navigation = navigationRef
  const { isSsoLoggedInUser } = useIsSsoLoggedInUser()
  const { data: userData } = useUserContext()
  const { leadSource } = useBrandStore((state) => state)
  const queryClient = useQueryClient()
  const brandId = params?.brandId
  const { data: content } = useBrandDetailsById(brandId || '')
  const shareButtonMessage = params?.shareButtonMessage
  const showScratchCard = params?.showScratchCard
  const [defaultLanguage, setDefaultLanguage] = useState(i18n.locale)
  const toast = useToast()
  const isWhatsappRef = useRef(false)
  const regEx = new RegExp('{{URL}}', 'g')

  const generateLinkParams = {
    partnerId: brandId,
    customerId: params?.customerId,
    reopen: params?.reopen,
    leadSource: leadSource,
    isEligibleBrand: params.isEligibleBrand,
    uiRankOfPartner: params?.eventPayload?.index,
    brandCategory: params?.brandCategory,
  }

  const eventPayload = showScratchCard
    ? {
        item_name: 'brands_list_share_after_adding_customer',
        brand_name: content?.name,
        brand_category: content?.categoryId,
        language: defaultLanguage,
      }
    : params?.eventPayload

  const { data: customerData } = useFetchCustomerDetails(params?.customerId)
  const leadIdRef = useRef('')
  const shareImage = params?.shareImage

  const { setIsBrandsFlow } = useMyCustomersStore((state) => state)

  useEffect(() => {
    if (showScratchCard) {
      setIsBrandsFlow(false)
    }
  }, [setIsBrandsFlow, showScratchCard])

  const {
    mutate: shareLinkMutation,
    data: shareLinkData,
    isLoading,
  } = useGenerateSharableLink()
  const {
    mutate: globalShareLinkMutation,
    data: globalShareLinkData,
    isLoading: globalIsLoading,
  } = useGenerateGlobalSharableLink()

  useEffect(() => {
    if (showScratchCard) {
      publishEvent('screen_view', {
        screen_name: 'share_card_link_by_adding_customer',
      })
    }
  }, [showScratchCard])

  useEffect(() => {
    if (params?.routeName === 'missingFields') {
      shareLinkMutation(params?.generateLinkParams)
    }
  }, [])

  const [isBottomSheetVisible, setIsBottomSheetVisible] =
    useState<boolean>(false)
  const [isShareARNBottomSheetVisible, setIsShareARNBottomSheetVisible] =
    useState(false)
  const [isConsentVisible, setIsConsentVisible] = useState<boolean>(false)
  const [bottomSheetType, setBottomSheetType] = useState<string>('')

  const handleShare = (sharableLink: string, isForCustomerFlow = false) => {
    shareButtonMessage
      ? onShare(
          shareButtonMessage?.[defaultLanguage]
            ?.replace(regEx, sharableLink + ' ')
            ?.replace('{{agentName}}', userData?.fullName)
            ?.replace('{{agentPhoneNumber}}', '' + getAgentPhoneNumber())
            ?.replace('{{customerName}}', '' + customerData?.fullName),
          isForCustomerFlow,
          isWhatsappRef.current,
        )
      : onShare(
          content?.shareContent[defaultLanguage]
            ?.replace(regEx, sharableLink + ' ')
            ?.replace('{{agentName}}', userData?.fullName)
            ?.replace('{{agentPhoneNumber}}', '' + getAgentPhoneNumber()) ||
            content?.shareContent?.message
              ?.replace(regEx, sharableLink + ' ')
              ?.replace('{{agentName}}', userData?.fullName)
              ?.replace('{{agentPhoneNumber}}', '' + getAgentPhoneNumber()) ||
            '',
          isForCustomerFlow,
          isWhatsappRef.current,
        )
    if (params?.customerId) setIsLinkShared(true)
  }

  const handleLeadAdded = async () => {
    if (APP_NAME !== MY_TEAMS_ADVISOR) {
      const { logGAEvent } = await import('@zet/analytics/google-analytics')
      const { logFbEvent } = await import('@zet/analytics/facebook')

      logGAEvent('lead_added')
      logFbEvent('leadadded', {
        leadData: shareLinkData,
        eventPayload,
      })
    }
  }

  useEffect(() => {
    if (params?.customerId) {
      if (shareLinkData) {
        handleLeadAdded()
        if (shareLinkData?.statusCode === 3320) {
          replace('CrossSelling', {
            brandId,
            customerName: customerData?.fullName,
            notEligibleReason: shareLinkData?.message,
            customerId: params?.customerId,
          })
        } else if (shareLinkData?.statusCode === 3309) {
          setBottomSheetType('notEligible')
          setIsBottomSheetVisible(true)
        } else if (shareLinkData?.statusCode === 3361) {
          setIsConsentVisible(true)
        } else if (shareLinkData?.statusCode === 3326) {
          replace('MissingFieldsPopup', {
            customerName: customerData?.fullName,
            customerId: params?.customerId,
            brandId,
            brandName: content?.name,
            generateLinkParams: {
              partnerId: brandId,
              customerId: params?.customerId,
              reopen: params?.reopen,
              leadSource: leadSource,
              isEligibleBrand: params.isEligibleBrand,
              uiRankOfPartner: params?.eventPayload?.index,
            },
          })
        } else if (
          (shareLinkData?.url && shareLinkData?.statusCode === 3307) ||
          (shareLinkData?.url && shareLinkData?.statusCode === 3308)
        ) {
          setBottomSheetType('missingDetails')
          setIsBottomSheetVisible(true)
        } else if (shareLinkData?.status === 409) {
          setBottomSheetType('alreadyApplied')
          setIsBottomSheetVisible(true)
        } else if (shareLinkData?.statusCode === 3323) {
          setBottomSheetType('generic')
          setIsBottomSheetVisible(true)
        } else if (shareLinkData?.url) {
          leadIdRef.current = shareLinkData?.leadId || ''
          handleShare(shareLinkData?.url, true)
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    globalShareLinkData?.link,
    shareLinkData?.status,
    shareLinkData?.statusCode,
    shareLinkData?.url,
  ])

  const { replace } = useNavigation<LoggedInNavigation>()
  const { setIsLinkShared } = useBrandStore((state) => state)
  const { data: shareCreativeImage } = useShareCreative(
    brandId,
    defaultLanguage,
  )
  const [imageBase64, setImageBase64] = useState('')

  useEffect(() => {
    if (shareCreativeImage) {
      readBase64Data()
    }
  }, [shareCreativeImage])

  async function validateImage(url: string): Promise<boolean> {
    try {
      const success = await Image.prefetch(url)
      return success
    } catch {
      return false
    }
  }

  const readBase64Data = async () => {
    try {
      const imageUrl = shareImage || shareCreativeImage

      if (!imageUrl) {
        console.error('No image URL provided')
        setImageBase64('')
        return
      }

      // First validate the image
      const isValid = await validateImage(imageUrl)
      if (!isValid) {
        console.warn('Invalid or corrupted image:', imageUrl)
        setImageBase64('')
        return
      }

      let base64Data = ''

      if (imageUrl.startsWith('data:')) {
        base64Data = imageUrl.split(',')[1]
      } else {
        const res = await BlobUtil.config({ fileCache: true }).fetch(
          'GET',
          imageUrl,
        )
        base64Data = await res.base64()
      }

      if (!base64Data || base64Data.trim() === '') {
        setImageBase64('')
        return
      }

      const filePath = `${BlobUtil.fs.dirs.CacheDir}/shared_image_${Date.now()}.png`
      await BlobUtil.fs.writeFile(filePath, base64Data, 'base64')

      setImageBase64('file://' + filePath)
    } catch (error) {
      console.error('Error processing image:', error)
      setImageBase64('')
    }
  }

  const onShare = async (
    data: string,
    isForCustomerFlow = false,
    isWhatsapp = false,
  ) => {
    const shareConfig = {
      isWhatsapp,
      phoneNumber: params?.customerPhoneNumber,
      message: data,
      ...(imageBase64 ? { base64Image: imageBase64 } : {}),
    }

    try {
      queryClient.invalidateQueries({ queryKey: ['home-widgets'] })
      queryClient.invalidateQueries(['leads'])
      queryClient.invalidateQueries(['guaranteedCustomerList', brandId])
      queryClient.invalidateQueries(['guaranteedLeadList', brandId])
      queryClient.invalidateQueries([
        'useHighGuaranteedEligibleCustomers',
        brandId,
      ])
      queryClient.invalidateQueries(['useFetchHighGuaranteedPartners'])
      queryClient.invalidateQueries(['GetDemoSaleState'])

      replace('BrandShareDetails', {
        brandId: brandId,
        customerName: customerData?.fullName,
        customerId: params?.customerId ?? null,
        ...(params?.customerPhoneNumber
          ? { customerPhoneNumber: params?.customerPhoneNumber }
          : {}),
        leadId: leadIdRef.current,
        routeName: isForCustomerFlow ? 'forCustomerFlow' : 'category',
        shareConfig: {
          ...(imageBase64 ? { imageBase64 } : {}),
          message: data,
        },
      })
      Platform.OS !== 'web' && shareToApp(shareConfig, toast)
    } catch (error) {
      console.warn(error)
    }
  }

  const handlePress = (isSkipShareContentARNBottomsheet = false) => {
    if (
      !isSkipShareContentARNBottomsheet &&
      (APP_NAME === MY_TEAMS_ADVISOR
        ? content?.subAgentContent?.shareLinkConfirmation?.show
        : content?.shareLinkConfirmation?.show)
    ) {
      setIsShareARNBottomSheetVisible(true)
    } else if (params?.customerId) {
      shareLinkMutation({
        partnerId: brandId,
        customerId: params?.customerId,
        reopen: params?.reopen,
        leadSource: leadSource,
        isEligibleBrand: params.isEligibleBrand,
        uiRankOfPartner: params?.eventPayload?.index,
        variantId: params?.variantId,
        brandCategory: params?.brandCategory,
      })
    } else {
      globalShareLinkMutation(
        {
          partnerId: brandId,
          customerAdditionSource: params?.source,
          variantId: params?.variantId,
          leadSource: leadSource,
        },
        {
          onSuccess: (globalShareLinkData) => {
            handleShare(globalShareLinkData?.link, false)
          },
        },
      )
    }
  }

  const getAgentPhoneNumber = () => {
    const parentAgentPhoneNumber = String(
      storageUtil.getObject('parentAgentPhoneNumber'),
    )

    const helplineNumber = String(storageUtil.getObject('helplineNumber'))

    if (isSsoLoggedInUser && helplineNumber) {
      return helplineNumber
    }

    if (isSsoLoggedInUser && parentAgentPhoneNumber) {
      return parentAgentPhoneNumber.slice(2, parentAgentPhoneNumber.length)
    }

    return userData?.phoneNumber
  }

  return (
    <>
      <ScrollView
        className="bg-white px-3"
        showsVerticalScrollIndicator={false}
      >
        {showScratchCard && (
          <Stack className="items-center justify-center pt-3">
            <Icon type="DoneOutlined" color={zetColors.success[100]} />
            <SH1 className="mt-3 text-center">
              {i18n.t('shareCardLink.headerForBrandsDlow', {
                name: params?.customerName,
                partnerName: content?.name,
              })}
            </SH1>
          </Stack>
        )}
        <B2 className="my-3">{i18n.t('shareCardLink.headerDesc')}</B2>
        <YStack className="h-200 w-full rounded bg-[#E9FFE6] p-4">
          {shareCreativeImage || shareImage ? (
            <Image
              source={{ uri: shareImage || shareCreativeImage }}
              className="w-full rounded-lg"
              resizeMode="contain"
              style={{
                height: 310,
              }}
            />
          ) : (
            <></>
          )}
          <Stack className="mt-3">
            <SH2 className="mb-1">{i18n.t('shareCardLink.namaste')}</SH2>
            {shareButtonMessage ? (
              <B2>
                {normalizeMultilineText(
                  shareButtonMessage?.[defaultLanguage]
                    ?.replace(regEx, '')
                    ?.replace('{{agentName}}', userData?.fullName)
                    ?.replace(
                      '{{agentPhoneNumber}}',
                      '' + getAgentPhoneNumber(),
                    )
                    ?.replace('{{customerName}}', '' + customerData?.fullName),
                )}
              </B2>
            ) : (
              <B2>
                {normalizeMultilineText(
                  content?.shareContent[defaultLanguage]
                    // ?.replace('{{URL}}', link ?? '')
                    ?.replace(regEx, '')
                    ?.replace('{{agentName}}', userData?.fullName)
                    ?.replace(
                      '{{agentPhoneNumber}}',
                      '' + getAgentPhoneNumber(),
                    ) ||
                    content?.shareContent?.message
                      ?.replace(regEx, '')
                      ?.replace('{{agentName}}', userData?.fullName)
                      ?.replace(
                        '{{agentPhoneNumber}}',
                        '' + getAgentPhoneNumber(),
                      ) ||
                    '',
                )}
              </B2>
            )}
          </Stack>
        </YStack>
        <Stack className="my-3">
          <B2>{i18n.t('shareCardLink.languageSelection')}</B2>
          <XStack className="mt-3">
            <Pressable
              className={`w-[40%] rounded-xl border p-2 ${
                defaultLanguage === 'en'
                  ? 'border-primary-100'
                  : 'border-grays-30'
              }`}
              onPress={() => setDefaultLanguage('en')}
              eventName="click_action"
              eventPayload={{
                item_name: 'share_card_link_change_language',
                language: 'English',
              }}
            >
              <XStack className="items-center justify-center">
                <EnglishLanguage />
                <B2 className="text-grays-60 ml-2">
                  {i18n.t('shareCardLink.english')}
                </B2>
              </XStack>
            </Pressable>
            <Pressable
              className={`ml-3 w-[40%] rounded-xl border p-2 ${
                defaultLanguage === 'hi'
                  ? 'border-primary-100'
                  : 'border-grays-30'
              }`}
              onPress={() => setDefaultLanguage('hi')}
              eventName="click_action"
              eventPayload={{
                item_name: 'share_card_link_change_language',
                language: 'Hindi',
              }}
            >
              <XStack className="items-center justify-center align-middle">
                <HindiLanguage />
                <B2 className="text-grays-60 ml-2">
                  {i18n.t('shareCardLink.hindi')}
                </B2>
              </XStack>
            </Pressable>
          </XStack>
        </Stack>
      </ScrollView>
      {params?.isDisclaimerVisible && (
        <XStack className={'bg-primary-10 px-3 pb-1 pt-2'}>
          <Icon type="AnnounceOutlined" size={24} />
          <B5 className="flex-1">
            <SH4>{i18n.t('note')}:</SH4> {i18n.t('shareCardLink.disclaimer')}
          </B5>
        </XStack>
      )}

      <ShareButtons
        content={content}
        params={params}
        customerData={customerData}
        eventPayload={eventPayload}
        handlePress={handlePress}
        isLoading={isLoading || globalIsLoading}
        isWhatsappRef={isWhatsappRef}
      />
      {isLoading || globalIsLoading ? (
        <Modal visible={isLoading || globalIsLoading} transparent={true}>
          <Stack
            className="bg-grays-100 flex-1 justify-center"
            style={{ opacity: 0.8 }}
          >
            <ActivityIndicator size={'large'} color={'white'} />
            <SH3 className="mx-3 mt-6 text-center text-white">
              {i18n.t('shareCardLink.loaderText')}
            </SH3>
          </Stack>
        </Modal>
      ) : (
        <></>
      )}
      <BottomSheet
        visible={isBottomSheetVisible}
        disableBottomSheet={() => setIsBottomSheetVisible(false)}
        onDismiss={() => setIsBottomSheetVisible(false)}
        sheetName="share_card_link"
      >
        <>
          <Stack className="mb-3 mt-3 self-center">
            {bottomSheetType !== 'generic' ? (
              <RegistrationForm />
            ) : (
              <RegistrationFormCheck />
            )}
          </Stack>
          <B2 className="mx-3 mb-3 text-center">
            {bottomSheetType === 'missingDetails'
              ? i18n.t('shareCardLink.missingDetails')
              : bottomSheetType === 'alreadyApplied'
                ? i18n.t('shareCardLink.alreadyApplied')
                : shareLinkData?.message}
          </B2>
          <Stack className="border-grays-30 border-t px-3 py-3">
            {bottomSheetType === 'missingDetails' && (
              <Button
                className="w-full"
                size="lg"
                onPress={() => {
                  setIsBottomSheetVisible(false)
                  navigation.navigate('WebViewScreen', {
                    url: shareLinkData?.url,
                  })
                }}
              >
                {i18n.t('shareCardLink.fill')}
              </Button>
            )}
            {bottomSheetType === 'notEligible' ||
            bottomSheetType === 'alreadyApplied' ||
            bottomSheetType === 'generic' ? (
              <Button
                className="w-full"
                size="lg"
                onPress={() => setIsBottomSheetVisible(false)}
              >
                {i18n.t('shareCardLink.okay')}
              </Button>
            ) : (
              <></>
            )}
          </Stack>
        </>
      </BottomSheet>
      <ArnBottomSheet
        isVisible={isShareARNBottomSheetVisible}
        brandComms={content}
        brandId={brandId}
        onContinue={() => handlePress(true)}
        setIsVisible={setIsShareARNBottomSheetVisible}
      />
      {isConsentVisible && (
        <UserConsent
          brandId={brandId}
          customerId={params?.customerId}
          customerName={customerData?.fullName}
          customerPhoneNumber={params?.customerPhoneNumber}
          isBottomSheetVisible={isConsentVisible}
          setIsBottomSheetVisible={setIsConsentVisible}
          leadSource={leadSource}
          generateLinkParams={generateLinkParams}
        />
      )}
    </>
  )
}

export default ShareCardLink
